// src/pages/hr/HRWorkforce.jsx
import React, { useState, useEffect } from "react";
import { HR_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { collection, query, onSnapshot, orderBy, limit, where } from "firebase/firestore";
import { db } from "../../firebase/config";

const PANEL_LABEL = "HR Panel";

const TABS = ["Attendance", "Leave Management", "Shift Roster", "Holidays", "Overtime"];

export default function HRWorkforce() {
  const [tab, setTab] = useState("Attendance");
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [users, setUsers] = useState([]);
  const todayStr = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const u1 = onSnapshot(collection(db, "users"), (s) => setUsers(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(query(collection(db, "attendance"), where("date", "==", todayStr)), (s) => setAttendance(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
    const u3 = onSnapshot(query(collection(db, "leaveRequests"), orderBy("createdAt", "desc"), limit(30)), (s) => setLeaves(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); u3(); };
  }, [todayStr]);

  const userMap = Object.fromEntries(users.map((u) => [u.uid, u]));
  const pendingLeaves = leaves.filter((l) => l.status === "pending");

  return (
    <Layout navItems={HR_NAV} panelLabel={PANEL_LABEL}>
      <style>{`
        .wf-tabs { display: flex; gap: 0.5rem; flex-wrap: wrap; border-bottom: 2px solid var(--border, #e5e7eb); margin-bottom: 1.5rem; }
        .wf-tab { padding: 0.55rem 1rem; background: none; border: none; font-weight: 600; font-size: 0.83rem; cursor: pointer; color: var(--text-muted, #6b7280); border-bottom: 3px solid transparent; margin-bottom: -2px; white-space: nowrap; transition: all 0.2s; }
        .wf-tab.active { color: #1b4b31; border-bottom-color: #1b4b31; }
        .wf-table { width: 100%; border-collapse: collapse; font-size: 0.84rem; }
        .wf-table th { background: var(--bg-secondary, #f9fafb); text-align: left; padding: 0.65rem 0.9rem; font-size: 0.74rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid var(--border, #e5e7eb); }
        .wf-table td { padding: 0.7rem 0.9rem; border-bottom: 1px solid var(--border, #f3f4f6); vertical-align: middle; color: var(--text-primary, #111); }
        .wf-table tr:hover td { background: var(--bg-secondary, #f9fafb); }
        .wf-status { display: inline-block; padding: 0.18rem 0.6rem; border-radius: 99px; font-size: 0.72rem; font-weight: 700; }
        .wf-status--present { background: #d1fae5; color: #065f46; }
        .wf-status--absent { background: #fee2e2; color: #991b1b; }
        .wf-status--pending { background: #fef3c7; color: #92400e; }
        .wf-status--approved { background: #d1fae5; color: #065f46; }
        .wf-status--rejected { background: #fee2e2; color: #991b1b; }
        .wf-coming-soon { text-align: center; padding: 4rem 2rem; color: var(--text-muted); }
        .wf-coming-soon-icon { font-size: 3rem; margin-bottom: 1rem; }
        .wf-coming-soon-title { font-size: 1.1rem; font-weight: 700; color: var(--text-primary, #111); margin-bottom: 0.5rem; }
      `}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">Workforce Management</h1>
          <p className="page-subtitle">Attendance, leaves, shifts, and scheduling control center</p>
        </div>
      </div>

      <div className="wf-tabs">
        {TABS.map((t) => (
          <button key={t} className={`wf-tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === "Attendance" && (
        <div>
          <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
            <div className="hr-stat-card" style={{ flex: 1, minWidth: 140, borderTop: "3px solid #10b981" }}>
              <div className="hr-stat-icon" style={{ color: "#10b981" }}>✅</div>
              <div className="hr-stat-body">
                <div className="hr-stat-value">{attendance.filter(a => a.clockIn).length}</div>
                <div className="hr-stat-label">Present Today</div>
              </div>
            </div>
            <div className="hr-stat-card" style={{ flex: 1, minWidth: 140, borderTop: "3px solid #ef4444" }}>
              <div className="hr-stat-icon" style={{ color: "#ef4444" }}>❌</div>
              <div className="hr-stat-body">
                <div className="hr-stat-value">{Math.max(users.length - attendance.filter(a => a.clockIn).length, 0)}</div>
                <div className="hr-stat-label">Absent Today</div>
              </div>
            </div>
          </div>
          <table className="wf-table">
            <thead><tr><th>Employee</th><th>Clock In</th><th>Clock Out</th><th>Status</th></tr></thead>
            <tbody>
              {attendance.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>No attendance records for today</td></tr>
              ) : attendance.map((a) => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 600 }}>{userMap[a.userId]?.displayName || a.userId}</td>
                  <td>{a.clockIn || "—"}</td>
                  <td>{a.clockOut || "—"}</td>
                  <td><span className={`wf-status wf-status--${a.clockIn ? "present" : "absent"}`}>{a.clockIn ? "Present" : "Absent"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "Leave Management" && (
        <div>
          <h3 style={{ margin: "0 0 0.75rem", fontSize: "0.9rem", fontWeight: 700 }}>Pending Approvals ({pendingLeaves.length})</h3>
          <table className="wf-table">
            <thead><tr><th>Employee</th><th>Leave Type</th><th>From</th><th>To</th><th>Reason</th><th>Status</th></tr></thead>
            <tbody>
              {pendingLeaves.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>No pending requests 🎉</td></tr>
              ) : pendingLeaves.map((l) => (
                <tr key={l.id}>
                  <td style={{ fontWeight: 600 }}>{l.employeeName || userMap[l.userId]?.displayName || l.userId}</td>
                  <td>{l.leaveType}</td>
                  <td>{l.startDate}</td>
                  <td>{l.endDate}</td>
                  <td style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.reason || "—"}</td>
                  <td><span className="wf-status wf-status--pending">Pending</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {["Shift Roster", "Holidays", "Overtime"].includes(tab) && (
        <div className="wf-coming-soon">
          <div className="wf-coming-soon-icon">🚧</div>
          <div className="wf-coming-soon-title">{tab} — Coming Soon</div>
          <p>This module is under development and will be available soon.</p>
        </div>
      )}
    </Layout>
  );
}
